import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@soroush/ui';
import { apiFetch } from '../lib/api';
import {
  airportPlace,
  fallbackAirports,
  isIranianAirport,
  toAirport,
  type Airport,
} from '../lib/airports';
import { toFaDigits } from '../lib/jalali';
import { FlightDateField } from './FlightDateField';

type Route = { origin: string; destination: string };
type TripType = 'oneWay' | 'roundTrip' | 'multiCity';
type Panel = 'book' | 'checkin' | 'trips';
type Leg = { origin: string; destination: string; date: string };
type FieldError = { origin?: string; destination?: string; date?: string };

const MAX_TRAVELLERS = 9;
const MAX_LEGS = 4;

function uniqueAirports(codes: string[]): Airport[] {
  return [...new Set(codes)].map(toAirport);
}

function defaultDate(offset = 1) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

function airportFromQuery(code: string | null, fallback: string) {
  const normalized = code?.toUpperCase() ?? '';
  return /^[A-Z]{3}$/.test(normalized) ? normalized : fallback;
}

function queryCount(value: string | null, min: number, max: number, fallback: number) {
  const count = Number(value);
  return value !== null && Number.isInteger(count) && count >= min && count <= max
    ? count
    : fallback;
}

function paxFromQuery(params: URLSearchParams) {
  const total = queryCount(params.get('passengers'), 1, MAX_TRAVELLERS, 1);
  const infants = queryCount(params.get('infants'), 0, MAX_TRAVELLERS, 0);
  const children = queryCount(params.get('children'), 0, MAX_TRAVELLERS, 0);
  const adults = total - children - infants;
  if (adults < 1 || infants > adults) return { adults: total, children: 0, infants: 0 };
  return { adults, children, infants };
}

function parseLeg(raw: string): Leg | null {
  const [origin, destination, date] = raw.split('.');
  if (!/^[A-Z]{3}$/.test(origin ?? '') || !/^[A-Z]{3}$/.test(destination ?? '')) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date ?? '')) return null;
  return { origin, destination, date };
}

function useRoutes(): Route[] | null {
  const [routes, setRoutes] = useState<Route[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    apiFetch<{ routes: Route[] }>('/api/v1/search/routes')
      .then((data) => {
        if (!cancelled && data.routes.length > 0) setRoutes(data.routes);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  return routes;
}

export function BookingBar({ pending = false }: { pending?: boolean }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isFa = i18n.language.startsWith('fa');
  const tomorrow = useMemo(() => defaultDate(), []);
  const routes = useRoutes();

  const initialLegs = params.get('trip') === 'multi' ? params.getAll('leg').map(parseLeg).filter((leg): leg is Leg => Boolean(leg)) : [];
  const [panel, setPanel] = useState<Panel>('book');
  const [tripType, setTripType] = useState<TripType>(
    initialLegs.length >= 2 ? 'multiCity' : params.get('returnDate') ? 'roundTrip' : 'oneWay',
  );
  const [origin, setOrigin] = useState(() => airportFromQuery(params.get('origin'), 'THR'));
  const [destination, setDestination] = useState(() =>
    airportFromQuery(params.get('destination'), 'MHD'),
  );
  const [departureDate, setDepartureDate] = useState(() => params.get('departureDate') || tomorrow);
  const [returnDate, setReturnDate] = useState(() => params.get('returnDate') ?? '');
  const [legs, setLegs] = useState<Leg[]>(() =>
    initialLegs.length >= 2
      ? initialLegs
      : [
          { origin: 'THR', destination: 'MHD', date: tomorrow },
          { origin: 'MHD', destination: 'THR', date: '' },
        ],
  );
  const [pax, setPax] = useState(() => paxFromQuery(params));
  const [openPicker, setOpenPicker] = useState<string | null>(null);
  const [routeError, setRouteError] = useState<FieldError>({});
  const [legErrors, setLegErrors] = useState<FieldError[]>([]);
  const [checkin, setCheckin] = useState({ reference: '', flight: '' });
  const [checkinError, setCheckinError] = useState<{ reference?: string; flight?: string }>({});
  const [trips, setTrips] = useState({
    pnr: '',
    lastName: '',
    date: '',
  });
  const [tripsError, setTripsError] = useState<{ pnr?: string; lastName?: string; date?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const busy = pending || submitting;

  useEffect(() => {
    if (!pending) setSubmitting(false);
  }, [pending]);

  const travellers = pax.adults + pax.children + pax.infants;
  const cabin = params.get('cabin') || 'economy';

  const originAirports = useMemo(
    () => (routes ? uniqueAirports(routes.map((r) => r.origin)) : fallbackAirports),
    [routes],
  );

  function destinationsFor(from: string) {
    return routes
      ? uniqueAirports(routes.filter((r) => r.origin === from).map((r) => r.destination))
      : fallbackAirports;
  }

  useEffect(() => {
    if (!routes || tripType === 'multiCity') return;
    const nextOrigin = routes.some((r) => r.origin === origin) ? origin : routes[0]!.origin;
    const served = routes.filter((r) => r.origin === nextOrigin);
    const nextDestination = served.some((r) => r.destination === destination)
      ? destination
      : served[0]!.destination;
    if (nextOrigin !== origin) setOrigin(nextOrigin);
    if (nextDestination !== destination) setDestination(nextDestination);
  }, [routes, origin, destination, tripType]);

  function setCount(kind: 'adults' | 'children' | 'infants', value: number) {
    setPax((current) => {
      const next = { ...current, [kind]: value };
      if (next.infants > next.adults) next.infants = next.adults;
      const total = next.adults + next.children + next.infants;
      if (total > MAX_TRAVELLERS) return current;
      return next;
    });
  }

  function switchTrip(next: TripType) {
    setTripType(next);
    setOpenPicker(null);
    setRouteError({});
    setLegErrors([]);
    if (next === 'multiCity') {
      setLegs((current) => {
        if (current.some((leg) => leg.origin && leg.date)) return current;
        return [
          { origin, destination, date: departureDate },
          { origin: destination, destination: origin, date: '' },
        ];
      });
    }
    if (next === 'oneWay') setReturnDate('');
  }

  function updateLeg(index: number, patch: Partial<Leg>) {
    setLegs((current) => current.map((leg, i) => (i === index ? { ...leg, ...patch } : leg)));
    setLegErrors((current) => current.map((err, i) => (i === index ? {} : err)));
  }

  function appendQuery(qs: URLSearchParams) {
    qs.set('cabin', cabin);
    qs.set('passengers', String(travellers));
    if (pax.children > 0) qs.set('children', String(pax.children));
    if (pax.infants > 0) qs.set('infants', String(pax.infants));
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (panel === 'checkin') {
      const next: typeof checkinError = {};
      if (checkin.reference.trim().length < 5) next.reference = t('common.requiredField');
      if (!/^[A-Za-z0-9]{2,8}$/.test(checkin.flight.trim())) next.flight = t('common.requiredField');
      setCheckinError(next);
      if (Object.keys(next).length) return;
      const qs = new URLSearchParams({
        mode: 'checkin',
        pnr: checkin.reference.trim().toUpperCase(),
        flight: checkin.flight.trim().toUpperCase(),
      });
      navigate(`/manage?${qs}`);
      return;
    }
    if (panel === 'trips') {
      const next: typeof tripsError = {};
      if (!/^[A-Za-z0-9]{5,6}$/.test(trips.pnr.trim())) next.pnr = t('common.requiredField');
      if (trips.lastName.trim().length < 2) next.lastName = t('common.requiredField');
      if (!trips.date) next.date = t('common.dateRequired');
      setTripsError(next);
      if (Object.keys(next).length) return;
      const qs = new URLSearchParams({
        pnr: trips.pnr.trim().toUpperCase(),
        lastName: trips.lastName.trim(),
        date: trips.date,
      });
      navigate(`/manage?${qs}`);
      return;
    }

    if (tripType === 'multiCity') {
      const errors = legs.map((leg) => {
        const err: FieldError = {};
        if (!leg.origin) err.origin = t('common.originRequired');
        if (!leg.destination) err.destination = t('common.destinationRequired');
        if (!leg.date) err.date = t('common.dateRequired');
        if (leg.origin && leg.origin === leg.destination) err.destination = t('common.destinationRequired');
        return err;
      });
      setLegErrors(errors);
      if (errors.some((err) => Object.keys(err).length)) return;
      setSubmitting(true);
      const qs = new URLSearchParams({ trip: 'multi' });
      for (const leg of legs) qs.append('leg', `${leg.origin}.${leg.destination}.${leg.date}`);
      appendQuery(qs);
      navigate(`/search?${qs}`, { state: { searchId: Date.now() } });
      return;
    }

    const err: FieldError = {};
    if (!origin) err.origin = t('common.originRequired');
    if (!destination || origin === destination) err.destination = t('common.destinationRequired');
    if (!departureDate) err.date = t('common.dateRequired');
    if (tripType === 'roundTrip' && !returnDate) {
      setOpenPicker('return');
      err.date = t('common.dateRequired');
    }
    setRouteError(err);
    if (Object.keys(err).length) return;

    setSubmitting(true);
    const qs = new URLSearchParams({
      origin: origin.toUpperCase(),
      destination: destination.toUpperCase(),
      departureDate,
    });
    if (tripType === 'roundTrip' && returnDate) qs.set('returnDate', returnDate);
    appendQuery(qs);
    navigate(`/search?${qs}`, { state: { searchId: Date.now() } });
  }

  const dateLabels = {
    departureLabel: tripType === 'roundTrip' ? t('common.outboundDate') : t('common.departureDate'),
    returnLabel: t('common.returnDate'),
    selectLabel: t('common.selectDate'),
    addReturnLabel: t('common.addReturnDate'),
    jalaliLabel: t('common.jalaliCalendar'),
    gregorianLabel: t('common.gregorianCalendar'),
    switchToGregorianLabel: t('common.switchToGregorian'),
    switchToJalaliLabel: t('common.switchToJalali'),
    closeLabel: t('common.closeCalendar'),
    prevMonthLabel: t('common.calendarPrevMonth'),
    nextMonthLabel: t('common.calendarNextMonth'),
  };

  return (
    <form
      className={`bw${openPicker ? ' is-open' : ''}`}
      onSubmit={onSubmit}
      aria-label={t('common.findFlight')}
    >
      <div className="bw__tabs" role="tablist">
        <TabButton
          active={panel === 'book'}
          onClick={() => setPanel('book')}
          icon={<TabIcon name="airplane" />}
          label={t('common.buyTicket')}
        />
        <TabButton
          active={panel === 'checkin'}
          onClick={() => setPanel('checkin')}
          icon={<TabIcon name="seat" />}
          label={t('common.onlineCheckin')}
        />
        <TabButton
          active={panel === 'trips'}
          onClick={() => setPanel('trips')}
          icon={<TabIcon name="list" />}
          label={t('common.myTrips')}
        />
      </div>

      {panel === 'book' ? (
        <div className="bw__panel">
          <div className="bw__types" role="radiogroup" aria-label={t('common.searchFlights')}>
            {(
              [
                ['oneWay', t('common.oneWay')],
                ['roundTrip', t('common.roundTrip')],
                ['multiCity', t('common.multiCity')],
              ] as const
            ).map(([value, label]) => (
              <label key={value} className={`bw__radio${tripType === value ? ' is-on' : ''}`}>
                <input
                  type="radio"
                  name="tripType"
                  value={value}
                  checked={tripType === value}
                  onChange={() => switchTrip(value)}
                />
                <span className="bw__radio-dot" aria-hidden="true" />
                {label}
              </label>
            ))}
          </div>

          {tripType === 'multiCity' ? (
            <div className="bw__legs">
              {legs.map((leg, index) => (
                <div className="bw__leg" key={index}>
                  {legs.length > 2 ? (
                    <button
                      type="button"
                      className="bw__remove"
                      aria-label={t('common.removeFlight')}
                      onClick={() => setLegs((current) => current.filter((_, i) => i !== index))}
                    >
                      <CloseIcon />
                    </button>
                  ) : null}
                  <RouteBox
                    idPrefix={`leg-${index}`}
                    origin={leg.origin}
                    destination={leg.destination}
                    origins={originAirports}
                    destinations={destinationsFor(leg.origin)}
                    isFa={isFa}
                    open={openPicker}
                    onOpen={setOpenPicker}
                    originError={legErrors[index]?.origin}
                    destinationError={legErrors[index]?.destination}
                    onOrigin={(code) => updateLeg(index, { origin: code })}
                    onDestination={(code) => updateLeg(index, { destination: code })}
                    onSwap={() =>
                      updateLeg(index, { origin: leg.destination, destination: leg.origin })
                    }
                  />
                  <FlightDateField
                    id={`leg-${index}-date`}
                    variant="single"
                    departure={leg.date}
                    isFa={isFa}
                    open={openPicker === `date-${index}`}
                    invalid={Boolean(legErrors[index]?.date)}
                    error={legErrors[index]?.date}
                    min={index > 0 ? legs[index - 1]?.date : undefined}
                    onOpenChange={(next) => setOpenPicker(next ? `date-${index}` : null)}
                    onDeparture={(iso) => updateLeg(index, { date: iso })}
                    {...dateLabels}
                  />
                </div>
              ))}
              <div className="bw__footer">
                <button
                  type="button"
                  className="bw__add"
                  disabled={legs.length >= MAX_LEGS}
                  onClick={() =>
                    setLegs((current) => [
                      ...current,
                      {
                        origin: current.at(-1)?.destination ?? '',
                        destination: '',
                        date: '',
                      },
                    ])
                  }
                >
                  <PlusIcon />
                  {t('common.addFlight')}
                </button>
                <PaxField
                  pax={pax}
                  isFa={isFa}
                  open={openPicker === 'pax'}
                  onOpenChange={(next) => setOpenPicker(next ? 'pax' : null)}
                  onCount={setCount}
                />
                <SearchSubmit busy={busy} label={t('common.findFlight')} busyLabel={t('common.searchingFlights')} />
              </div>
            </div>
          ) : (
            <div className="bw__row">
              <RouteBox
                idPrefix="bb"
                origin={origin}
                destination={destination}
                origins={originAirports}
                destinations={destinationsFor(origin)}
                isFa={isFa}
                open={openPicker}
                onOpen={setOpenPicker}
                originError={routeError.origin}
                destinationError={routeError.destination}
                onOrigin={(code) => {
                  setOrigin(code);
                  setRouteError((err) => ({ ...err, origin: undefined }));
                }}
                onDestination={(code) => {
                  setDestination(code);
                  setRouteError((err) => ({ ...err, destination: undefined }));
                }}
                onSwap={() => {
                  setOrigin(destination);
                  setDestination(origin);
                }}
              />
              <FlightDateField
                id="bb-date"
                variant="range"
                roundTrip={tripType === 'roundTrip'}
                departure={departureDate}
                returnDate={returnDate}
                isFa={isFa}
                open={openPicker === 'date' || openPicker === 'return'}
                invalid={Boolean(routeError.date)}
                error={routeError.date}
                onOpenChange={(next) => setOpenPicker(next ? 'date' : null)}
                onDeparture={(iso) => {
                  setDepartureDate(iso);
                  if (returnDate && returnDate < iso) setReturnDate('');
                  setRouteError((err) => ({ ...err, date: undefined }));
                }}
                onReturn={(iso) => {
                  setReturnDate(iso);
                  setRouteError((err) => ({ ...err, date: undefined }));
                }}
                onAddReturn={() => switchTrip('roundTrip')}
                {...dateLabels}
              />
              <PaxField
                pax={pax}
                isFa={isFa}
                open={openPicker === 'pax'}
                onOpenChange={(next) => setOpenPicker(next ? 'pax' : null)}
                onCount={setCount}
              />
              <SearchSubmit busy={busy} label={t('common.findFlight')} busyLabel={t('common.searchingFlights')} />
            </div>
          )}
        </div>
      ) : null}

      {panel === 'checkin' ? (
        <div className="bw__panel">
          <p className="bw__lead">{t('common.checkinLead')}</p>
          <div className="bw__row bw__row--lookup">
            <TextField
              id="checkin-ref"
              label={t('common.bookingOrTicket')}
              value={checkin.reference}
              error={checkinError.reference}
              onChange={(value) => setCheckin((c) => ({ ...c, reference: value }))}
              requiredMark
            />
            <TextField
              id="checkin-flight"
              label={t('common.checkinFlightNumber')}
              value={checkin.flight}
              error={checkinError.flight}
              onChange={(value) => setCheckin((c) => ({ ...c, flight: value.toUpperCase() }))}
              dir="ltr"
              requiredMark
            />
            <div className="bw__submit">
              <Button type="submit">{t('common.findFlight')}</Button>
            </div>
          </div>
        </div>
      ) : null}

      {panel === 'trips' ? (
        <div className="bw__panel">
          <p className="bw__lead">
            {t('common.loginToSeeTrips')}{' '}
            <Link to="/login">{t('common.login')}</Link>
            . {t('common.tripsLookupLead')}
          </p>
          <div className="bw__row bw__row--lookup">
            <TextField
              id="trips-pnr"
              label={t('common.pnr')}
              value={trips.pnr}
              error={tripsError.pnr}
              onChange={(value) => setTrips((c) => ({ ...c, pnr: value.toUpperCase() }))}
              dir="ltr"
              requiredMark
            />
            <TextField
              id="trips-name"
              label={t('common.tripsLastName')}
              value={trips.lastName}
              error={tripsError.lastName}
              onChange={(value) => setTrips((c) => ({ ...c, lastName: value }))}
              dir="ltr"
              requiredMark
            />
            <FlightDateField
              id="trips-date"
              variant="single"
              departure={trips.date}
              isFa={isFa}
              open={openPicker === 'trips-date'}
              invalid={Boolean(tripsError.date)}
              error={tripsError.date}
              onOpenChange={(next) => setOpenPicker(next ? 'trips-date' : null)}
              onDeparture={(iso) => setTrips((c) => ({ ...c, date: iso }))}
              {...dateLabels}
            />
            <div className="bw__submit">
              <Button type="submit">{t('common.findFlight')}</Button>
            </div>
          </div>
        </div>
      ) : null}
    </form>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={`bw__tab${active ? ' is-active' : ''}`}
      onClick={onClick}
    >
      {icon}
      {label}
    </button>
  );
}

function RouteBox({
  idPrefix,
  origin,
  destination,
  origins,
  destinations,
  isFa,
  open,
  onOpen,
  originError,
  destinationError,
  onOrigin,
  onDestination,
  onSwap,
}: {
  idPrefix: string;
  origin: string;
  destination: string;
  origins: Airport[];
  destinations: Airport[];
  isFa: boolean;
  open: string | null;
  onOpen: (key: string | null) => void;
  originError?: string;
  destinationError?: string;
  onOrigin: (code: string) => void;
  onDestination: (code: string) => void;
  onSwap: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className={`bw__route${originError || destinationError ? ' is-invalid' : ''}`}>
      <span className="bw__route-rule" aria-hidden="true" />
      <AirportField
        id={`${idPrefix}-origin`}
        label={t('common.origin')}
        placeholder={t('common.searchCity')}
        airports={origins}
        value={origin}
        disabledCode={destination}
        isFa={isFa}
        open={open === `${idPrefix}-origin`}
        error={originError}
        onOpenChange={(next) => onOpen(next ? `${idPrefix}-origin` : null)}
        onChange={onOrigin}
      />
      <button type="button" className="bw__swap" onClick={onSwap} aria-label={t('common.swapRoute')}>
        <SwapIcon />
      </button>
      <AirportField
        id={`${idPrefix}-dest`}
        label={t('common.destination')}
        placeholder={t('common.searchCity')}
        airports={destinations}
        value={destination}
        disabledCode={origin}
        isFa={isFa}
        open={open === `${idPrefix}-dest`}
        error={destinationError}
        onOpenChange={(next) => onOpen(next ? `${idPrefix}-dest` : null)}
        onChange={onDestination}
      />
    </div>
  );
}

function AirportField({
  id,
  label,
  placeholder,
  airports,
  value,
  disabledCode,
  isFa,
  open,
  error,
  onOpenChange,
  onChange,
}: {
  id: string;
  label: string;
  placeholder: string;
  airports: Airport[];
  value: string;
  disabledCode?: string;
  isFa: boolean;
  open: boolean;
  error?: string;
  onOpenChange: (open: boolean) => void;
  onChange: (code: string) => void;
}) {
  const { t } = useTranslation();
  const listboxId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches,
  );
  const selected = value ? (airports.find((a) => a.code === value) ?? toAirport(value)) : null;

  const options = useMemo(() => {
    const q = query.trim().toLowerCase();
    return airports.filter((airport) => {
      if (airport.code === disabledCode) return false;
      if (!q) return true;
      const place = airportPlace(airport, true) + airportPlace(airport, false);
      return (
        airport.code.toLowerCase().includes(q) ||
        place.toLowerCase().includes(q) ||
        airport.cityFa.includes(query.trim())
      );
    });
  }, [airports, disabledCode, query]);

  const domestic = options.filter((airport) => isIranianAirport(airport.code));
  const international = options.filter((airport) => !isIranianAirport(airport.code));

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 760px)');
    const apply = () => setNarrow(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    const timer = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open || !narrow) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open, narrow]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      const target = e.target as Node;
      if (!rootRef.current?.contains(target) && !menuRef.current?.contains(target)) onOpenChange(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onOpenChange(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onOpenChange]);

  function selectAirport(code: string) {
    onChange(code);
    onOpenChange(false);
  }

  function onSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (options.length ? (i + 1) % options.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (options.length ? (i - 1 + options.length) % options.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const airport = options[activeIndex];
      if (airport) selectAirport(airport.code);
    }
  }

  return (
    <div className={`bw__airport${open ? ' is-open' : ''}${error ? ' is-invalid' : ''}`} ref={rootRef}>
      <button
        type="button"
        id={id}
        className={`bw__airport-trigger${selected ? ' has-value' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        onClick={() => onOpenChange(!open)}
      >
        <PinIcon />
        <span>{selected ? airportPlace(selected, isFa) : label}</span>
        {selected ? <span className="bw__float">{label}</span> : null}
      </button>
      {error ? <p className="bw__error">{error}</p> : null}
      {open
        ? (() => {
            const menu = (
              <div
                ref={menuRef}
                className={narrow ? 'bw__menu bw__sheet' : 'bw__menu'}
                role={narrow ? 'dialog' : undefined}
                aria-modal={narrow ? true : undefined}
                aria-label={label}
              >
                <div className="bw__sheet-bar">
                  <p>{label}</p>
                  <button
                    type="button"
                    className="bw__sheet-close"
                    aria-label={t('common.close')}
                    onClick={() => onOpenChange(false)}
                  >
                    <CloseIcon />
                  </button>
                </div>
                <div className="bw__menu-search">
                  <input
                    ref={searchRef}
                    type="search"
                    value={query}
                    placeholder={placeholder}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={onSearchKeyDown}
                    aria-controls={listboxId}
                    autoComplete="off"
                  />
                </div>
                <div id={listboxId} className="bw__menu-cols" role="listbox" aria-label={label}>
                  {options.length === 0 ? (
                    <p className="bw__menu-empty">{t('common.noCityResults')}</p>
                  ) : (
                    <>
                      <AirportColumn
                        title={t('common.domesticFlights')}
                        airports={domestic}
                        isFa={isFa}
                        value={value}
                        options={options}
                        activeIndex={activeIndex}
                        onActive={setActiveIndex}
                        onSelect={selectAirport}
                      />
                      <AirportColumn
                        title={t('common.internationalFlights')}
                        airports={international}
                        isFa={isFa}
                        value={value}
                        options={options}
                        activeIndex={activeIndex}
                        onActive={setActiveIndex}
                        onSelect={selectAirport}
                      />
                    </>
                  )}
                </div>
              </div>
            );
            return narrow ? createPortal(menu, document.body) : menu;
          })()
        : null}
    </div>
  );
}

function AirportColumn({
  title,
  airports,
  isFa,
  value,
  options,
  activeIndex,
  onActive,
  onSelect,
}: {
  title: string;
  airports: Airport[];
  isFa: boolean;
  value: string;
  options: Airport[];
  activeIndex: number;
  onActive: (index: number) => void;
  onSelect: (code: string) => void;
}) {
  if (airports.length === 0) return null;
  return (
    <div className="bw__menu-col">
      <p className="bw__menu-title">{title}</p>
      <ul>
        {airports.map((airport) => {
          const index = options.findIndex((item) => item.code === airport.code);
          const selected = airport.code === value;
          return (
            <li key={airport.code}>
              <button
                type="button"
                role="option"
                aria-selected={selected}
                className={`bw__menu-item${selected ? ' is-selected' : ''}${
                  index === activeIndex ? ' is-active' : ''
                }`}
                onMouseEnter={() => onActive(index)}
                onClick={() => onSelect(airport.code)}
              >
                <span>{airportPlace(airport, isFa)}</span>
                <bdi>{airport.code}</bdi>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function PaxField({
  pax,
  isFa,
  open,
  onOpenChange,
  onCount,
}: {
  pax: { adults: number; children: number; infants: number };
  isFa: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCount: (kind: 'adults' | 'children' | 'infants', value: number) => void;
}) {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const total = pax.adults + pax.children + pax.infants;
  const label = isFa ? `${toFaDigits(total)} مسافر` : total === 1 ? '1 passenger' : `${total} passengers`;

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) onOpenChange(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, onOpenChange]);

  const rows = [
    { kind: 'adults' as const, title: t('common.adults'), hint: t('common.adultsAge'), min: 1 },
    { kind: 'children' as const, title: t('common.child'), hint: t('common.childrenAge'), min: 0 },
    { kind: 'infants' as const, title: t('common.infant'), hint: t('common.infantsAge'), min: 0 },
  ];

  return (
    <div className={`bw__pax${open ? ' is-open' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="bw__box bw__pax-trigger"
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}
      >
        <UserIcon />
        <span>{label}</span>
      </button>
      {open ? (
        <div className="bw__pax-panel">
          <p className="bw__pax-title">{t('common.selectPassengers')}</p>
          {rows.map((row) => {
            const value = pax[row.kind];
            const atCap = total >= MAX_TRAVELLERS;
            const infantCap = row.kind === 'infants' && value >= pax.adults;
            return (
              <div className="bw__pax-row" key={row.kind}>
                <div>
                  <strong>{row.title}</strong>
                  <span>{row.hint}</span>
                </div>
                <div className="bw__stepper">
                  <button
                    type="button"
                    aria-label="-"
                    disabled={value <= row.min}
                    onClick={() => onCount(row.kind, value - 1)}
                  >
                    −
                  </button>
                  <span>{isFa ? toFaDigits(value) : value}</span>
                  <button
                    type="button"
                    aria-label="+"
                    disabled={atCap || infantCap}
                    onClick={() => onCount(row.kind, value + 1)}
                  >
                    +
                  </button>
                </div>
              </div>
            );
          })}
          <button type="button" className="bw__pax-ok" onClick={() => onOpenChange(false)}>
            {t('common.confirm')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function TextField({
  id,
  label,
  value,
  error,
  onChange,
  dir,
  requiredMark,
}: {
  id: string;
  label: string;
  value: string;
  error?: string;
  onChange: (value: string) => void;
  dir?: 'ltr' | 'rtl';
  requiredMark?: boolean;
}) {
  return (
    <div className={`bw__text${error ? ' is-invalid' : ''}${value ? ' has-value' : ''}`}>
      <label className="bw__box" htmlFor={id}>
        <input id={id} value={value} dir={dir} onChange={(e) => onChange(e.target.value)} />
        <span className={value ? 'bw__float' : 'bw__placeholder'}>
          {label}
          {requiredMark ? <i aria-hidden="true"> *</i> : null}
        </span>
      </label>
      {error ? <p className="bw__error">{error}</p> : null}
    </div>
  );
}

const TAB_GLYPHS = {
  airplane:
    'M896 278v84l-342 214v234c0 36-28 64-64 64-34 0-64-28-64-64v-234l-340-214v-84l340 106v-234l-84-64v-64l148 42 150-42v64l-86 64v234z',
  seat: 'M192 692c-38 30-46 80-18 118 26 40 78 48 116 22s52-82 22-120c-26-38-78-46-120-20M730 132h-304c-64 0-94 14-132 106-38 94-154 390-154 390h-84c0 0 110-286 158-402 54-136 110-180 216-180h304M726 302h-210c0 0-102 248-132 296-30 46-64 50-82 46-64 0-98-60-76-110 22-52 76-192 98-252s64-102 124-102h294l162-128 64 64',
  list: 'M345.094 788.32v-194.14h584.71v194.14M345.094 107.68h584.71v194.14h-584.71M345.094 349.79h584.71v196.428h-584.71M100.702 594.18h196.428v194.14h-196.428M100.702 107.68h196.428v194.14h-196.428M100.702 349.79h196.428v196.428h-196.428z',
} as const;

function TabIcon({ name }: { name: keyof typeof TAB_GLYPHS }) {
  return (
    <span className="bw__tab-glyph icon-font" aria-hidden="true">
      <svg viewBox="0 0 1024 1024" width="24" height="24" focusable="false">
        <g transform="matrix(1 0 0 -1 0 1024)">
          <path fill="currentColor" d={TAB_GLYPHS[name]} />
        </g>
      </svg>
    </span>
  );
}

function PinIcon() {
  return (
    <svg className="bw__pin" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        d="M12 21s7-6.1 7-11a7 7 0 1 0-14 0c0 4.9 7 11 7 11z"
      />
      <circle cx="12" cy="10" r="2.15" fill="none" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

function SwapIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
      <path
        className="bw__swap-h"
        fill="currentColor"
        d="M7.5 8.5h11.2l-2.1-2.1 1.1-1.1L22 8.9l-4.3 3.6-1.1-1.1 2.1-2.1H7.5V8.5zm9 7H5.3l2.1 2.1-1.1 1.1L2 15.1l4.3-3.6 1.1 1.1-2.1 2.1h11.2V15.5z"
      />
      <path
        className="bw__swap-v"
        fill="currentColor"
        d="M8.2 3.4 4 7.6l1.2 1.2 2.2-2.2V20h1.6V6.6l2.2 2.2 1.2-1.2-4.2-4.2zm7.6 17.2 4.2-4.2-1.2-1.2-2.2 2.2V4h-1.6v13.4l-2.2-2.2-1.2 1.2 4.2 4.2z"
      />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg className="bw__glyph" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4zm0 2c-3.3 0-8 1.7-8 5v1h16v-1c0-3.3-4.7-5-8-5z"
      />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M9 4h2v5h5v2h-5v5H9v-5H4V9h5V4z" />
    </svg>
  );
}

function SearchSubmit({ busy, label, busyLabel }: { busy: boolean; label: string; busyLabel: string }) {
  return (
    <div className="bw__submit">
      <Button type="submit" disabled={busy} aria-busy={busy} aria-label={busy ? busyLabel : undefined}>
        <span className={busy ? 'bw__submit-label is-hidden' : 'bw__submit-label'}>{label}</span>
        {busy ? <span className="bw__spinner" aria-hidden="true" /> : null}
      </Button>
    </div>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M5.2 4.1 10 8.9l4.8-4.8 1.1 1.1L11.1 10l4.8 4.8-1.1 1.1L10 11.1l-4.8 4.8-1.1-1.1L8.9 10 4.1 5.2z"
      />
    </svg>
  );
}
